import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Mic, Sparkles } from "lucide-react";
import { useRef } from "react";

const HeroSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const imageOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.6]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] as const }
    }
  };

  return (
    <section ref={sectionRef} className="relative min-h-screen flex items-center pt-28 pb-20 overflow-hidden bg-cream">
      {/* Parallax gradient accent */}
      <motion.div 
        style={{ y: backgroundY }}
        className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-coral/10 via-peach/5 to-transparent rounded-full blur-3xl -translate-y-1/4 translate-x-1/4" 
      />
      
      <div className="max-w-7xl mx-auto px-6 w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ y: textY }}
            className="space-y-10"
          >
            {/* Badge */}
            <motion.div variants={itemVariants}>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-coral/10 text-coral rounded-full text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                Now available, free to use
              </span>
            </motion.div>

            <motion.h1 
              variants={itemVariants}
              className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.08] text-foreground tracking-tight"
            >
              Your brain forgets
              <br />
              your wins.
              <br />
              <span className="text-coral">Erlara doesn't.</span>
            </motion.h1>

            <motion.p 
              variants={itemVariants}
              className="text-xl text-muted-foreground max-w-lg leading-relaxed"
            >
              Capture your progress in seconds. Finally see the evidence that you're not failing, you're growing.
            </motion.p>

            <motion.div 
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 pt-2"
            >
              <a
                href="https://app.erlara.tech/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-primary text-primary-foreground rounded-full font-medium text-lg hover:opacity-90 transition-all hover:shadow-xl group"
              >
                Start Capturing Wins
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-border text-foreground rounded-full font-medium text-lg hover:border-primary/50 hover:bg-muted/50 transition-all"
              >
                See How It Works
              </a>
            </motion.div>

            {/* Trust message */}
            <motion.div variants={itemVariants} className="pt-2">
              <p className="text-sm text-muted-foreground">
                Free forever. No credit card required.
              </p>
            </motion.div>
          </motion.div>

          {/* Right Content - Hero Image with floating elements */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="relative"
          >
            {/* Main Image Container */}
            <motion.div 
              style={{ scale: imageScale, opacity: imageOpacity }}
              className="relative rounded-3xl overflow-hidden shadow-2xl"
            >
              <img
                src="/images/hero-image.jpg"
                srcSet="/images/hero-image-600.jpg 600w, /images/hero-image-800.jpg 800w, /images/hero-image.jpg 1080w"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 50vw"
                alt="Woman looking up with peaceful expression"
                className="w-full h-[500px] lg:h-[600px] object-cover object-center"
                fetchPriority="high"
              />

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-navy/30 via-navy/10 to-transparent" />

              {/* Floating UI Elements */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0, y: [0, -10, 0] }}
                transition={{ 
                  opacity: { duration: 0.6, delay: 0.8 },
                  x: { duration: 0.6, delay: 0.8 },
                  y: { duration: 4, repeat: Infinity, ease: "easeInOut" }
                }}
                className="absolute top-6 left-6 glass-card px-4 py-2 hidden sm:flex items-center gap-2"
              >
                <div className="w-8 h-8 rounded-full bg-coral-light flex items-center justify-center">
                  <span className="text-xs">⏱</span>
                </div>
                <span className="text-sm font-medium text-foreground">12:45 PM, Today</span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0, y: [0, -15, 0] }}
                transition={{ 
                  opacity: { duration: 0.6, delay: 1 },
                  x: { duration: 0.6, delay: 1 },
                  y: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }
                }}
                className="absolute top-6 right-6 glass-card px-4 py-3 max-w-[200px]"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-coral flex items-center justify-center">
                    <Mic className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        animate={{ scaleY: [1, 1.8, 1] }}
                        transition={{
                          duration: 0.5,
                          repeat: Infinity,
                          delay: i * 0.1,
                          ease: "easeInOut",
                        }}
                        className="w-1 h-4 bg-coral rounded-full origin-center"
                      />
                    ))}
                  </div>
                  <span className="text-sm font-mono text-foreground">00:04</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-coral animate-pulse" />
                  <span className="text-xs text-muted-foreground">Capture in under 5 seconds</span>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: [0, -12, 0] }}
                transition={{ 
                  opacity: { duration: 0.6, delay: 1.2 },
                  y: { duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }
                }}
                className="absolute bottom-6 right-6 glass-card px-5 py-4 max-w-[220px]"
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-coral flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="text-sm font-medium block truncate">Sarah M.</span>
                    <span className="text-xs text-muted-foreground">5:08</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground italic leading-tight">
                  "Finally seeing proof I'm making progress."
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: [0, -8, 0] }}
                transition={{ 
                  opacity: { duration: 0.6, delay: 1.4 },
                  y: { duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 2 }
                }}
                className="absolute bottom-6 left-6 hidden sm:flex items-center gap-3"
              >
                <div className="w-12 h-12 rounded-full border-4 border-coral bg-coral/20 flex items-center justify-center">
                  <div className="w-4 h-4 rounded-full bg-coral" />
                </div>
                <div className="w-10 h-10 rounded-xl bg-primary/90 flex items-center justify-center">
                  <span className="text-primary-foreground text-base">✦</span>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Bottom floating nav - Desktop only */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden lg:block"
      >
        <div className="glass-card px-8 py-4 flex items-center gap-8">
          <span className="flex items-center gap-2 text-sm font-medium text-foreground">
            <span className="w-2 h-2 rounded-full bg-coral" />
            Home
          </span>
          <a href="#how-it-works" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            How It Works
          </a>
          <a href="#who-its-for" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Who It's For
          </a>
          <a href="https://app.erlara.tech/" target="_blank" rel="noopener noreferrer" className="text-sm text-coral font-medium hover:text-coral/80 transition-colors">
            Try It Free →
          </a>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
